import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { selectEstadosGlobales } from 'store/catalogos';
import {
  Card,
  Form,
  Row,
  Spin,
  Col,
  Input,
  InputNumber,
  Tabs,
  DatePicker,
  Button,
  Divider,
  Modal,
  Typography,
  TreeSelect,
  Table as AntTable,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import { formatReceived } from 'utils/formatValues';
import toTitleCase from 'utils/toTitleCase';
import makeTreeFromCuentasContables from 'utils/makeTreeFromCuentasContables';
import {
  normalizeDataAreas,
  formatTreeSelectData,
  findInTree,
} from 'api/configuraciones/clavesPresupuestalesDeIngresos';
import {
  makeTreeFromClasificacionesObjetoGasto,
  normalizeClasificacionesObjetoGasto,
  extractIdClasificacionObjetoGasto,
} from 'utils/makeTreeFromClasificacionesObjetoGasto';
import {
  makeTreeFromFuentesDeFinanciamiento,
  normalizeFuentesDeFinanciamiento,
  extractIdFuenteDeFinanciamiento,
} from 'utils/makeTreeFromFuentesDeFinanciamiento';

const BASE_CABECERA = 'presupuestos/traspasos-o-modificaciones-presupuestales-de-egresos/';
const BASE_REGISTRO = 'presupuestos/registros-de-traspaso-o-modificacion-presupuestal-de-egresos/';
const { TabPane } = Tabs;
const btnLinkStyle = { border: 'none', boxShadow: 'none', padding: '0 8px' };

const MESES = [
  { id: 1, nombre: 'ENERO' },
  { id: 2, nombre: 'FEBRERO' },
  { id: 3, nombre: 'MARZO' },
  { id: 4, nombre: 'ABRIL' },
  { id: 5, nombre: 'MAYO' },
  { id: 6, nombre: 'JUNIO' },
  { id: 7, nombre: 'JULIO' },
  { id: 8, nombre: 'AGOSTO' },
  { id: 9, nombre: 'SEPTIEMBRE' },
  { id: 10, nombre: 'OCTUBRE' },
  { id: 11, nombre: 'NOVIEMBRE' },
  { id: 12, nombre: 'DICIEMBRE' },
];

const mensajeDeError = (err, porDefecto) => {
  const data = err?.response?.data;
  if (!data) return porDefecto;
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) return data.join(' ');
  if (data.detail) return Array.isArray(data.detail) ? data.detail.join(' ') : data.detail;
  return Object.values(data).flat().join(' ') || porDefecto;
};

const moneyFormatter = (value) => {
  if (value === undefined || value === null || value === '') return '';
  return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const moneyParser = (value) => {
  if (!value) return '';
  return value.replace(/\$\s?|(,*)/g, '');
};

const normalizeSegmentoNombre = (nombre) => (nombre || '')
  .toUpperCase()
  .trim()
  .replace(/[ÁÀÄÂ]/g, 'A')
  .replace(/[ÉÈËÊ]/g, 'E')
  .replace(/[ÍÌÏÎ]/g, 'I')
  .replace(/[ÓÒÖÔ]/g, 'O')
  .replace(/[ÚÙÜÛ]/g, 'U');

const normalizeClaseCriTree = (data, parentPath = 'root') => {
  if (!data?.length) return [];
  return data.map((item, index) => {
    const currentPath = `${parentPath}-${item.id}-${index}`;
    const children = normalizeClaseCriTree(item.children, currentPath);
    const esHoja = !children.length;
    return {
      key: currentPath,
      value: currentPath,
      id: item.id,
      title: `${item.clave} - ${item.denominacion}`,
      disabled: !esHoja,
      selectable: esHoja,
      ...(children.length && { children }),
    };
  });
};

const normalizarCuentasTree = (data) => {
  if (!data?.length) return [];
  return data.map((item) => {
    const children = normalizarCuentasTree(item.children);
    const isLeaf = !children.length;
    return {
      ...item,
      title: `${item.clave} - ${item.nombre}`,
      value: item.id,
      key: item.id,
      children: children.length ? children : undefined,
      disabled: !isLeaf,
    };
  });
};

// eslint-disable-next-line react/prop-types
function ModificacionesPresupuestales({ permission }) {
  const estadosGlobales = useSelector(selectEstadosGlobales);
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);

  const [formCabecera] = Form.useForm();
  const [formRegistro] = Form.useForm();

  const [view, setView] = useState('list');
  const [activeTab, setActiveTab] = useState('modificaciones');
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [cabeceras, setCabeceras] = useState([]);
  const [selectedCabeceraKeys, setSelectedCabeceraKeys] = useState([]);

  const [currentCabecera, setCurrentCabecera] = useState(null);
  const [registros, setRegistros] = useState([]);
  const [selectedRegistroKeys, setSelectedRegistroKeys] = useState([]);

  const [registroModalVisible, setRegistroModalVisible] = useState(false);
  const [deleteCabeceraVisible, setDeleteCabeceraVisible] = useState(false);
  const [deleteRegistroVisible, setDeleteRegistroVisible] = useState(false);
  const [editingRegistro, setEditingRegistro] = useState(null);

  const [catalogoCOG, setCatalogoCOG] = useState([]);
  const [catalogoCOGTree, setCatalogoCOGTree] = useState([]);
  const [allClavesPres, setAllClavesPres] = useState([]);
  const [catalogoAreasTree, setCatalogoAreasTree] = useState([]);
  const [catalogoFuentesTree, setCatalogoFuentesTree] = useState([]);
  const [catalogoClaves, setCatalogoClaves] = useState([]);
  const [catalogoCuentasContables, setCatalogoCuentasContables] = useState([]);
  const [catalogoClasesCRITree, setCatalogoClasesCRITree] = useState([]);
  const [clavesHabilitadas, setClavesHabilitadas] = useState(false);
  const [areaAplica, setAreaAplica] = useState(true);
  // La cuenta se bloquea solo cuando viene la Cuenta de Modificación del presupuesto anual;
  // Nómina no la maneja, así que en ese caso se captura a mano.
  const [cuentaBloqueada, setCuentaBloqueada] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [resCabs, resRegs] = await Promise.all([
        API.get(BASE_CABECERA),
        API.get(BASE_REGISTRO),
      ]);
      const rawCabs = Array.isArray(resCabs.data)
        ? resCabs.data : (resCabs.data?.results || []);
      const rawRegs = Array.isArray(resRegs.data)
        ? resRegs.data : (resRegs.data?.results || []);

      const enriched = rawCabs.map((cab) => {
        const cabRegs = rawRegs.filter((r) => {
          const ref = r.traspaso_o_modificacion_presupuestal_de_egreso;
          const id = typeof ref === 'object' ? ref?.id : ref;
          return String(id) === String(cab.id);
        });
        const firstReg = cabRegs[0];
        // eslint-disable-next-line max-len
        const sumAumento = cabRegs.reduce((s, r) => s + parseFloat(r.aumento_de_importe || 0), 0);
        // eslint-disable-next-line max-len
        const sumDisminucion = cabRegs.reduce((s, r) => s + parseFloat(r.disminucion_de_importe || 0), 0);
        const presAnterior = firstReg?.presupuesto_anterior ?? cab.presupuesto_anterior;
        const aumento = cabRegs.length > 0 ? sumAumento : (cab.aumento ?? null);
        const disminucion = cabRegs.length > 0 ? sumDisminucion : (cab.disminucion ?? null);
        const vigente = presAnterior !== null && presAnterior !== undefined
          ? parseFloat(presAnterior) + parseFloat(aumento || 0) - parseFloat(disminucion || 0)
          : (cab.presupuesto_vigente ?? null);
        return {
          ...cab,
          _clave_visible: firstReg?.clave_presupuestal_visible
            || cab.clave_presupuestal_display || null,
          _cog: firstReg?.cog || cab.clasificador_objeto_gasto || null,
          _pres_anterior: presAnterior,
          _aumento: aumento,
          _disminucion: disminucion,
          _pres_vigente: vigente,
        };
      });
      setCabeceras(enriched);
    } catch (err) {
      onError(err);
    }
  }, []);

  const cargarDatosElabora = useCallback(async () => {
    try {
      const resUsuario = await API.get('usuarios/id/');
      const usuario = resUsuario.data;
      let plazaLabel = usuario.plaza_detalle || usuario.puesto_usuario || '';
      let areaLabel = usuario.usuario_area || '';
      const empleadoId = usuario.empleado;
      if (empleadoId) {
        try {
          const resDatoOp = await API.get('catalogos/dato-operacional-del-empleado/', {
            params: { empleado: empleadoId },
          });
          const datoOpRaw = resDatoOp.data;
          const datoOp = Array.isArray(datoOpRaw)
            ? datoOpRaw[0]
            : (datoOpRaw?.results?.[0] ?? datoOpRaw);
          if (datoOp?.plaza) {
            const resPlaza = await API.get(`catalogos/plaza/${datoOp.plaza}/`);
            const plaza = resPlaza.data;
            plazaLabel = plaza?.puesto_clave_y_descripcion || plazaLabel;
            areaLabel = plaza?.directorio_de_area_clave_y_nombre
              || plaza?.directorio_area_clave_y_nombre
              || areaLabel;
          }
        } catch { /* silencioso */ }
      }
      formRegistro.setFieldsValue({
        // eslint-disable-next-line max-len
        elaboro_display: `${usuario.numero_de_empleado || ''} - ${usuario.first_name} ${usuario.last_name}`.trim(),
        plaza_display: plazaLabel,
        area_display: areaLabel,
      });
    } catch { /* silencioso */ }
  }, [formRegistro]);

  const fetchRegistros = useCallback(async (cabeceraId) => {
    try {
      const res = await API.get(BASE_REGISTRO, {
        params: { traspaso_o_modificacion_presupuestal_de_egreso: cabeceraId },
      });
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setRegistros(data);
    } catch {
      setRegistros([]);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadAll = async () => {
      try {
        setLoading(true);
        // eslint-disable-next-line max-len
        const [resCOGUnion, resAreas, resFuentes, resCuentas, resCri, resSegConfig] = await Promise.all([
          API.get('contabilidad/clasificaciones-objeto-gasto-union/'),
          API.get('catalogos/directorio-de-areas/'),
          API.get('contabilidad/clasificaciones-de-fuentes-de-financiamiento/'),
          API.get('contabilidad/cuentas-contables/'),
          API.get('contabilidad/cri/').catch(() => ({ data: [] })),
          API.get('configuraciones/configuraciones-de-segmentos-presupuestales/', {
            params: { estados_globales: 4, tipo_de_presupuesto: 2 },
          }).catch(() => ({ data: [] })),
        ]);
        if (mounted) {
          const cogUnionData = resCOGUnion.data || [];
          setCatalogoCOG(cogUnionData.map((c) => ({
            ...c,
            label_display: `${c.clave} - ${c.concepto}`,
          })));
          const cogTree = makeTreeFromClasificacionesObjetoGasto(cogUnionData);
          setCatalogoCOGTree(normalizeClasificacionesObjetoGasto(cogTree));
          const configs = resSegConfig.data || [];
          if (configs.length) {
            const resCP = await API.get('configuraciones/clave-presupuestal/', {
              params: { configuracion_de_segmento_presupuestal: configs[0].id },
            }).catch(() => ({ data: [] }));
            // El listado de Nómina devuelve los ids sin el sufijo _id
            setAllClavesPres((resCP.data || [])
              .filter((c) => !c.configuracion_de_segmento_presupuestal
                || c.configuracion_de_segmento_presupuestal === configs[0].id)
              .map((c) => ({
                ...c,
                clasificador_de_objeto_de_gasto_id: c.clasificador_de_objeto_de_gasto_id
                  ?? c.clasificador_de_objeto_de_gasto,
                directorio_de_area_id: c.directorio_de_area_id ?? c.directorio_de_area,
                fuente_financiamiento_id: c.fuente_financiamiento_id ?? c.fuente_financiamiento,
              })));

            const resSegmentos = await API.get(
              `configuraciones/configuraciones-de-segmentos-presupuestales/${configs[0].id}/segmentos/`,
            ).catch(() => ({ data: [] }));
            // El área puede venir como "Área" (SAC) o "Unidad Responsable" (Nómina): aplica si
            // cualquiera de los dos segmentos aplica.
            const segsArea = (resSegmentos.data || [])
              .filter((s) => ['AREA', 'UNIDAD RESPONSABLE'].includes(normalizeSegmentoNombre(s.nombre)));
            if (segsArea.length) setAreaAplica(segsArea.some((s) => s.el_segmento_aplica));
          }
          setCatalogoAreasTree(
            formatTreeSelectData(normalizeDataAreas(resAreas.data || [])),
          );
          const fuentesTree = makeTreeFromFuentesDeFinanciamiento(resFuentes.data || []);
          setCatalogoFuentesTree(normalizeFuentesDeFinanciamiento(fuentesTree));
          setCatalogoCuentasContables(
            normalizarCuentasTree(makeTreeFromCuentasContables(resCuentas.data || [])),
          );
          setCatalogoClasesCRITree(normalizeClaseCriTree(resCri.data || []));
        }
        await fetchAll();
      } catch (err) {
        onError(err, setLoading);
        return;
      }
      if (mounted) setLoading(false);
    };
    loadAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const loadClavesFiltradas = useCallback((cog, area, fuente, claveActual) => {
    if (!cog && !area && !fuente) {
      setCatalogoClaves([]);
      setClavesHabilitadas(false);
      formRegistro.setFieldsValue({ clave_presupuestal: undefined });
      return;
    }
    let filtered = [...allClavesPres];
    if (cog) filtered = filtered.filter((c) => c.clasificador_de_objeto_de_gasto_id === cog);
    if (area) filtered = filtered.filter((c) => c.directorio_de_area_id === area);
    if (fuente) filtered = filtered.filter((c) => c.fuente_financiamiento_id === fuente);
    const mapped = filtered.map((c) => ({
      ...c,
      label_clave: c.clave_visible || c.clave_completa || `ID: ${c.id}`,
    }));
    setCatalogoClaves(mapped);
    setClavesHabilitadas(mapped.length > 0);
    if (claveActual && !mapped.find((c) => c.id === claveActual)) {
      formRegistro.setFieldsValue({ clave_presupuestal: undefined });
    } else {
      formRegistro.setFieldsValue({ clave_presupuestal: claveActual ?? undefined });
    }
  }, [allClavesPres, formRegistro]);

  const onChangeClave = useCallback(async (idClave) => {
    if (!idClave) return;
    try {
      const res = await API.get(`${BASE_CABECERA}presupuesto-anterior/`, {
        params: { clave_presupuestal: idClave },
      });
      const presAnterior = res.data?.monto_final ?? 0;
      formRegistro.setFieldsValue({ presupuesto_anterior: presAnterior });
      const aumento = parseFloat(formRegistro.getFieldValue('aumento_de_importe') || 0);
      const disminucion = parseFloat(formRegistro.getFieldValue('disminucion_de_importe') || 0);
      formRegistro.setFieldsValue({ presupuesto_vigente: presAnterior + aumento - disminucion });

      const cuentaModificacion = res.data?.cuenta_de_modificacion;
      if (cuentaModificacion?.id) {
        formRegistro.setFieldsValue({ cuenta_contable: cuentaModificacion.id });
        setCuentaBloqueada(true);
      } else {
        formRegistro.setFieldsValue({ cuenta_contable: undefined });
        setCuentaBloqueada(false);
      }
    } catch { /* sin presupuesto anterior */ }
  }, [formRegistro]);

  const calcularPresupuestoVigente = useCallback(() => {
    const anterior = parseFloat(formRegistro.getFieldValue('presupuesto_anterior') || 0);
    const aumento = parseFloat(formRegistro.getFieldValue('aumento_de_importe') || 0);
    const disminucion = parseFloat(formRegistro.getFieldValue('disminucion_de_importe') || 0);
    formRegistro.setFieldsValue({ presupuesto_vigente: anterior + aumento - disminucion });
  }, [formRegistro]);

  const cabeceraRowSelection = useMemo(() => ({
    selectedRowKeys: selectedCabeceraKeys,
    type: 'checkbox',
    onChange: (keys) => setSelectedCabeceraKeys(keys),
  }), [selectedCabeceraKeys]);

  const handleCabeceraRowClick = useCallback((record) => {
    setSelectedCabeceraKeys((prev) => (prev.includes(record.id) ? [] : [record.id]));
  }, []);

  const registroRowSelection = useMemo(() => ({
    selectedRowKeys: selectedRegistroKeys,
    type: 'radio',
    onChange: (keys) => setSelectedRegistroKeys(keys),
  }), [selectedRegistroKeys]);

  const handleRegistroRowClick = useCallback((record) => {
    setSelectedRegistroKeys((prev) => (prev.includes(record.id) ? [] : [record.id]));
  }, []);

  const onClickAdd = useCallback(() => {
    setSelectedCabeceraKeys([]);
    setCurrentCabecera(null);
    setRegistros([]);
    setSelectedRegistroKeys([]);
    formCabecera.resetFields();
    formCabecera.setFieldsValue({ fecha: dayjs() });
    if (periodoFiscalSelected?.id) {
      formCabecera.setFieldsValue({ periodo: periodoFiscalSelected.id });
    }
    setView('detail');
  }, [formCabecera, periodoFiscalSelected]);

  // El doble clic en el grid manda la fila (sus dos clics alternan la selección);
  // el botón Editar no manda nada y usa la fila seleccionada.
  const onClickEdit = useCallback(async (fila) => {
    const cabId = fila?.id ?? selectedCabeceraKeys[0];
    if (!cabId) return;
    if (fila?.id) setSelectedCabeceraKeys([fila.id]);
    setLoading(true);
    try {
      const res = await API.get(`${BASE_CABECERA}${cabId}/`);
      const record = formatReceived(res.data);
      setCurrentCabecera(record);
      setActiveTab(record.es_modificacion ? 'modificaciones' : 'traspasos');
      formCabecera.setFieldsValue({
        ...record,
        fecha: record.fecha ? dayjs(record.fecha) : null,
      });
      await fetchRegistros(cabId);
      setView('detail');
    } catch (err) {
      onError(err, setLoading);
    }
    setLoading(false);
  }, [selectedCabeceraKeys, formCabecera, fetchRegistros]);

  const onClickDelete = useCallback(() => {
    if (!selectedCabeceraKeys.length) return;
    setDeleteCabeceraVisible(true);
  }, [selectedCabeceraKeys]);

  const onConfirmDeleteCabecera = useCallback(async () => {
    const [cabId] = selectedCabeceraKeys;
    if (!cabId) return;
    try {
      setLoading(true);
      const res = await API.delete(`${BASE_CABECERA}${cabId}/`);
      if (res?.status === 204) {
        onSuccess(res, 'Eliminado correctamente');
        setSelectedCabeceraKeys([]);
        setDeleteCabeceraVisible(false);
        await fetchAll();
      }
    } catch (err) {
      onError(err, setLoading);
    }
    setLoading(false);
  }, [selectedCabeceraKeys, fetchAll]);

  // ── Save cabecera (retorna la cabecera guardada, o null si no se pudo) ───────
  const onGuardarCabecera = useCallback(async () => {
    try {
      await formCabecera.validateFields();
    } catch {
      return null;
    }
    const values = formCabecera.getFieldsValue(true);
    const payload = {
      ...values,
      es_modificacion: activeTab === 'modificaciones',
      fecha: values.fecha
        ? (values.fecha.format?.('YYYY-MM-DD') ?? values.fecha)
        : null,
    };
    let cabeceraGuardada = null;
    try {
      setLoading(true);
      if (!currentCabecera?.id) {
        const res = await API.post(BASE_CABECERA, payload);
        if (res?.status === 201) {
          onSuccess(res, 'Cabecera creada correctamente');
          const nueva = formatReceived(res.data);
          setCurrentCabecera(nueva);
          formCabecera.setFieldsValue({
            ...nueva,
            fecha: nueva.fecha ? dayjs(nueva.fecha) : null,
          });
          await fetchAll();
          cabeceraGuardada = nueva;
        }
      } else {
        const res = await API.patch(`${BASE_CABECERA}${currentCabecera.id}/`, payload);
        if (res?.status === 200) {
          onSuccess(res, 'Actualizado correctamente');
          const updated = formatReceived(res.data);
          setCurrentCabecera(updated);
          await fetchAll();
          cabeceraGuardada = updated;
        }
      }
    } catch (err) {
      onError(err, setLoading);
    }
    setLoading(false);
    return cabeceraGuardada;
  }, [formCabecera, activeTab, currentCabecera, fetchAll]);

  const onAutorizar = useCallback(async () => {
    if (!currentCabecera?.id) return;
    try {
      setLoading(true);
      // eslint-disable-next-line max-len
      const res = await API.patch(`${BASE_CABECERA}${currentCabecera.id}/`, { estados_globales: 4 });
      const updated = formatReceived(res.data);
      setCurrentCabecera(updated);
      formCabecera.setFieldsValue({
        ...updated,
        fecha: updated.fecha ? dayjs(updated.fecha) : null,
      });
      await fetchRegistros(currentCabecera.id);
      await fetchAll();
      onSuccess(res, 'Autorizado correctamente.');
    } catch (err) {
      const msg = mensajeDeError(err, 'Error al autorizar');
      Modal.error({ title: 'Error al Autorizar', content: msg });
    }
    setLoading(false);
  }, [currentCabecera, formCabecera, fetchRegistros, fetchAll]);

  const getCabIdsFromSelection = useCallback(
    () => selectedCabeceraKeys,
    [selectedCabeceraKeys],
  );

  const onAutorizarLista = useCallback(async () => {
    if (!selectedCabeceraKeys.length) return;
    const cabIds = getCabIdsFromSelection();
    try {
      setLoading(true);
      await Promise.all(
        cabIds.map((id) => API.patch(`${BASE_CABECERA}${id}/`, { estados_globales: 4 })),
      );
      setSelectedCabeceraKeys([]);
      await fetchAll();
      onSuccess({}, 'Autorizado correctamente.');
    } catch (err) {
      const msg = mensajeDeError(err, 'Error al autorizar');
      Modal.error({ title: 'Error al Autorizar', content: msg });
    }
    setLoading(false);
  }, [selectedCabeceraKeys, getCabIdsFromSelection, fetchAll]);

  const onCancelarLista = useCallback(async () => {
    if (!selectedCabeceraKeys.length) return;
    const cabIds = getCabIdsFromSelection();
    try {
      setLoading(true);
      await Promise.all(
        cabIds.map((id) => API.patch(`${BASE_CABECERA}${id}/`, { estados_globales: 5 })),
      );
      onSuccess({}, 'Cancelado correctamente');
      setSelectedCabeceraKeys([]);
      await fetchAll();
    } catch (err) {
      onError(err, setLoading);
    }
    setLoading(false);
  }, [selectedCabeceraKeys, getCabIdsFromSelection, fetchAll]);

  const onRegresar = useCallback(() => {
    setView('list');
    setCurrentCabecera(null);
    setRegistros([]);
    setSelectedRegistroKeys([]);
    setSelectedCabeceraKeys([]);
    formCabecera.resetFields();
    fetchAll();
  }, [formCabecera, fetchAll]);

  // ── Add registro (si la cabecera aún no existe, la guarda primero) ───────────
  const onClickAddRegistro = useCallback(async () => {
    let cabecera = currentCabecera;
    if (!cabecera?.id) {
      cabecera = await onGuardarCabecera();
      if (!cabecera?.id) return;
    }
    setEditingRegistro(null);
    formRegistro.resetFields();
    setCatalogoClaves([]);
    setClavesHabilitadas(false);
    await cargarDatosElabora();
    setRegistroModalVisible(true);
  }, [currentCabecera, onGuardarCabecera, formRegistro, cargarDatosElabora]);

  const onClickEditRegistro = useCallback(async (fila) => {
    const key = fila?.id ?? selectedRegistroKeys[0];
    if (!key) return;
    const record = registros.find((r) => r.id === key);
    if (!record) return;
    if (currentCabecera?.estados_globales === 4) return;
    if (fila?.id) setSelectedRegistroKeys([fila.id]);
    setEditingRegistro(record);

    const getId = (val) => (typeof val === 'object' && val !== null ? val.id : val);

    const cogId = getId(record.cog);
    const areaId = getId(record.area) || null;
    const fuenteId = getId(record.fuente_de_financiamiento);
    const claveId = getId(record.clave_presupuestal);

    formRegistro.setFieldsValue({
      cog: findInTree(catalogoCOGTree, cogId)?.value ?? cogId,
      area: areaId,
      fuente_de_financiamiento: findInTree(catalogoFuentesTree, fuenteId)?.value ?? fuenteId,
      clave_presupuestal: claveId,
      cuenta_contable: getId(record.cuenta_contable),
      clase_cri: findInTree(catalogoClasesCRITree, getId(record.clase_cri))?.value
        ?? getId(record.clase_cri),
      periodo: record.periodo,
      presupuesto_anterior: record.presupuesto_anterior,
      aumento_de_importe: record.aumento_de_importe,
      disminucion_de_importe: record.disminucion_de_importe,
      presupuesto_vigente: record.presupuesto_vigente,
      estados_globales: getId(record.estados_globales),
      elaboro_display: record.elaboro_display || '',
      plaza_display: record.plaza_display || '',
      area_display: record.area_display || '',
    });

    if (cogId || areaId || fuenteId) {
      loadClavesFiltradas(cogId, areaId, fuenteId, claveId);
    }
    setRegistroModalVisible(true);
  // eslint-disable-next-line max-len
  }, [selectedRegistroKeys, registros, currentCabecera, formRegistro, loadClavesFiltradas, catalogoCOGTree, catalogoFuentesTree, catalogoClasesCRITree]);

  const onClickDeleteRegistro = useCallback(() => {
    if (!selectedRegistroKeys.length || currentCabecera?.estados_globales === 4) return;
    setDeleteRegistroVisible(true);
  }, [selectedRegistroKeys, currentCabecera]);

  const onConfirmDeleteRegistro = useCallback(async () => {
    const [key] = selectedRegistroKeys;
    try {
      setModalLoading(true);
      const res = await API.delete(`${BASE_REGISTRO}${key}/`);
      if (res?.status === 204) {
        onSuccess(res, 'Registro eliminado correctamente');
        setSelectedRegistroKeys([]);
        setDeleteRegistroVisible(false);
        if (currentCabecera?.id) await fetchRegistros(currentCabecera.id);
        fetchAll();
      }
    } catch (err) {
      onError(err, setModalLoading);
    }
    setModalLoading(false);
  }, [selectedRegistroKeys, currentCabecera, fetchRegistros, fetchAll]);

  const onFinishRegistro = useCallback(async () => {
    try {
      await formRegistro.validateFields();
    } catch {
      return;
    }
    const values = formRegistro.getFieldsValue();
    const aumento = parseFloat(values.aumento_de_importe || 0);
    const disminucion = parseFloat(values.disminucion_de_importe || 0);
    if (aumento === 0 && disminucion === 0) {
      Modal.error({ content: 'Debe ingresar un Aumento o Disminución mayor a cero.' });
      return;
    }
    const {
      elaboro_display, // eslint-disable-line no-unused-vars
      plaza_display, // eslint-disable-line no-unused-vars
      area_display, // eslint-disable-line no-unused-vars
      area, // eslint-disable-line no-unused-vars
      presupuesto_vigente, // eslint-disable-line no-unused-vars
      ...rest
    } = values;

    const claveSeleccionada = catalogoClaves.find((c) => c.id === values.clave_presupuestal);
    const payload = {
      ...rest,
      cog: rest.cog ? Number(extractIdClasificacionObjetoGasto(rest.cog)) : null,
      fuente_de_financiamiento: rest.fuente_de_financiamiento
        ? Number(extractIdFuenteDeFinanciamiento(rest.fuente_de_financiamiento))
        : null,
      traspaso_o_modificacion_presupuestal_de_egreso: currentCabecera.id,
      clave_presupuestal_visible: claveSeleccionada?.clave_visible
        || claveSeleccionada?.label_clave
        || rest.clave_presupuestal_visible
        || '',
    };
    try {
      setModalLoading(true);
      if (!editingRegistro) {
        const res = await API.post(BASE_REGISTRO, payload);
        if (res?.status === 201) {
          onSuccess(res, 'Registro agregado correctamente');
          setRegistroModalVisible(false);
          formRegistro.resetFields();
          setSelectedRegistroKeys([]);
          await fetchRegistros(currentCabecera.id);
          fetchAll();
        }
      } else {
        const res = await API.patch(`${BASE_REGISTRO}${editingRegistro.id}/`, payload);
        if (res?.status === 200) {
          onSuccess(res, 'Registro actualizado correctamente');
          setRegistroModalVisible(false);
          formRegistro.resetFields();
          setSelectedRegistroKeys([]);
          await fetchRegistros(currentCabecera.id);
          fetchAll();
        }
      }
    } catch (err) {
      onError(err, setModalLoading);
    }
    setModalLoading(false);
  }, [formRegistro, currentCabecera, editingRegistro, fetchRegistros, fetchAll]);

  const balanceTraspasos = useMemo(() => {
    const sumAumento = registros.reduce((s, r) => s + parseFloat(r.aumento_de_importe || 0), 0);
    const sumDisminucion = registros
      .reduce((s, r) => s + parseFloat(r.disminucion_de_importe || 0), 0);
    return { sumAumento, sumDisminucion, diferencia: sumAumento - sumDisminucion };
  }, [registros]);

  const esTraspaso = activeTab === 'traspasos';
  const esModoNuevo = !currentCabecera?.id;
  const esAutorizado = currentCabecera?.estados_globales === 4;
  const tabBloqueado = !esModoNuevo || registros.length > 0;

  const puedeAutorizar = useMemo(() => {
    if (!currentCabecera?.id || currentCabecera.estados_globales !== 1) return false;
    if (esTraspaso) {
      return registros.length >= 2 && Math.abs(balanceTraspasos.diferencia) < 0.01;
    }
    return registros.length >= 1;
  }, [currentCabecera, esTraspaso, registros, balanceTraspasos]);

  const tituloDetalle = useMemo(() => {
    const tipo = esTraspaso
      ? 'TRASPASOS PRESUPUESTALES DE EGRESOS'
      : 'MODIFICACIONES PRESUPUESTALES DE EGRESOS';
    return toTitleCase(`${esModoNuevo ? 'Agregar' : 'Editar'} ${tipo}`);
  }, [esTraspaso, esModoNuevo]);

  const columnsBase = useMemo(() => [
    {
      titleText: 'Clave Presupuestal',
      dataIndex: '_clave_visible',
      key: '_clave_visible',
      width: 220,
      render: (val) => val || '—',
    },
    {
      titleText: 'Clasificador por Objeto del Gasto',
      dataIndex: '_cog',
      key: '_cog',
      width: 260,
      render: (val) => {
        if (!val) return '—';
        if (typeof val === 'object') {
          return toTitleCase(`${val.clave || ''} - ${val.concepto || ''}`.trim());
        }
        const found = catalogoCOG.find((c) => c.id === val);
        return found ? toTitleCase(found.label_display) : val;
      },
    },
    {
      titleText: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '—'),
    },
    {
      titleText: 'Folio Agrupador',
      dataIndex: 'folio_agrupador',
      key: 'folio_agrupador',
      width: 200,
      render: (val) => val || '—',
    },
    {
      titleText: 'Folio Presupuestal',
      dataIndex: 'folio',
      key: 'folio',
      width: 200,
      render: (val) => val || '—',
    },
    {
      titleText: 'Periodo',
      dataIndex: 'fecha',
      key: 'periodo',
      width: 150,
      render: (val) => {
        if (!val) return '—';
        const d = dayjs(val);
        const mes = MESES[d.month()];
        return mes ? toTitleCase(`${mes.nombre} ${d.year()}`) : '—';
      },
    },
    {
      titleText: 'Movimiento',
      dataIndex: 'es_modificacion',
      key: 'es_modificacion',
      width: 140,
      render: (val) => (val ? 'Modificación' : 'Traspaso'),
    },
    {
      titleText: 'Presupuesto Anterior',
      dataIndex: '_pres_anterior',
      key: '_pres_anterior',
      width: 180,
      render: (val) => (val !== null && val !== undefined ? moneyFormatter(val) : '—'),
    },
    {
      titleText: 'Aumentos',
      dataIndex: '_aumento',
      key: '_aumento',
      width: 140,
      render: (val) => (val !== null && val !== undefined ? moneyFormatter(val) : '—'),
    },
    {
      titleText: 'Disminuciones',
      dataIndex: '_disminucion',
      key: '_disminucion',
      width: 140,
      render: (val) => (val !== null && val !== undefined ? moneyFormatter(val) : '—'),
    },
    {
      titleText: 'Presupuesto Vigente',
      dataIndex: '_pres_vigente',
      key: '_pres_vigente',
      width: 180,
      render: (val) => (val !== null && val !== undefined ? moneyFormatter(val) : '—'),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 130,
      render: (val) => {
        if (!val) return '—';
        if (typeof val === 'object') return toTitleCase(val.descripcion) || '—';
        return toTitleCase(estadosGlobales.find((e) => e.id === val)?.descripcion) || '—';
      },
    },
  ], [estadosGlobales, catalogoCOG]);

  const registroColumns = useMemo(() => [
    {
      title: 'Fecha',
      key: 'fecha_cab',
      width: 120,
      render: () => (currentCabecera?.fecha
        ? dayjs(currentCabecera.fecha).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Folio Agrupador',
      key: 'folio_agrupador_cab',
      width: 180,
      render: () => currentCabecera?.folio_agrupador || '—',
    },
    {
      title: 'Folio Presupuestal',
      key: 'folio_cab',
      width: 200,
      render: () => currentCabecera?.folio || '—',
    },
    {
      title: 'Clave Presupuestal',
      dataIndex: 'clave_presupuestal_visible',
      key: 'clave_presupuestal_visible',
      width: 220,
      render: (val) => val || '—',
    },
    {
      title: 'Clasificador por Objeto del Gasto',
      dataIndex: 'cog',
      key: 'cog',
      width: 260,
      render: (val) => {
        if (!val) return '—';
        if (typeof val === 'object') {
          return toTitleCase(`${val.clave || ''} - ${val.concepto || ''}`.trim());
        }
        const found = catalogoCOG.find((c) => c.id === val);
        return found ? toTitleCase(found.label_display) : val;
      },
    },
    {
      title: 'Periodo',
      key: 'periodo_cab',
      width: 150,
      render: () => {
        const fecha = currentCabecera?.fecha;
        if (!fecha) return '—';
        const d = dayjs(fecha);
        const mes = MESES[d.month()];
        return mes ? toTitleCase(`${mes.nombre} ${d.year()}`) : '—';
      },
    },
    {
      title: 'Presupuesto Anterior',
      dataIndex: 'presupuesto_anterior',
      key: 'presupuesto_anterior',
      width: 180,
      render: (val) => moneyFormatter(val),
    },
    {
      title: 'Aumento',
      dataIndex: 'aumento_de_importe',
      key: 'aumento_de_importe',
      width: 140,
      render: (val) => moneyFormatter(val),
    },
    {
      title: 'Disminución',
      dataIndex: 'disminucion_de_importe',
      key: 'disminucion_de_importe',
      width: 140,
      render: (val) => moneyFormatter(val),
    },
    {
      title: 'Presupuesto Vigente',
      dataIndex: 'presupuesto_vigente',
      key: 'presupuesto_vigente',
      width: 180,
      render: (val) => moneyFormatter(val),
    },
    {
      title: 'Elaboró',
      dataIndex: 'elaboro_display',
      key: 'elaboro_display',
      width: 220,
      render: (val) => val || '—',
    },
    {
      title: 'Plaza Elaboró',
      dataIndex: 'plaza_display',
      key: 'plaza_display',
      width: 220,
      render: (val) => val || '—',
    },
    {
      title: 'Área Elaboró',
      dataIndex: 'area_display',
      key: 'area_display',
      width: 220,
      render: (val) => val || '—',
    },
    {
      title: 'Estado',
      key: 'estado_reg',
      width: 130,
      render: () => {
        const eg = currentCabecera?.estados_globales;
        if (!eg) return '—';
        if (typeof eg === 'object') return toTitleCase(eg.descripcion) || '—';
        return toTitleCase(estadosGlobales.find((e) => e.id === eg)?.descripcion) || '—';
      },
    },
  ], [estadosGlobales, catalogoCOG, currentCabecera]);

  const requiredRule = { required: true, message: 'El campo es requerido' };

  const cabeceraForm = (
    <Form layout="vertical" form={formCabecera} scrollToFirstError>
      <Row gutter={16}>
        <Col xs={24} sm={6}>
          <Form.Item name="fecha" label="Fecha" rules={[requiredRule]}>
            <DatePicker
              format="DD/MM/YYYY"
              placeholder=""
              style={{ width: '100%' }}
              disabled={!!currentCabecera?.id || esAutorizado}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={6}>
          <Form.Item name="folio_agrupador" label="Folio Agrupador">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="observaciones" label="Observaciones">
            <Input.TextArea rows={1} disabled={esAutorizado} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>

        {view === 'list' && (
          <Table
            cols={columnsBase}
            data={cabeceras}
            permission={permission}
            rowSelection={cabeceraRowSelection}
            handleOnRowClick={handleCabeceraRowClick}
            mobileColIndex={0}
            controls={{ onClickAdd, onClickEdit, onClickDelete }}
            customActions={[
              {
                onClick: onAutorizarLista,
                text: 'Autorizar',
                icon: <CheckCircleOutlined />,
                disabledBySelection: false,
                className: 'success',
              },
              {
                onClick: onCancelarLista,
                text: 'Cancelar',
                icon: <CloseCircleOutlined />,
                disabledBySelection: false,
                className: 'danger',
              },
            ]}
          />
        )}

        {view === 'detail' && (
          <Card
            className="form-container"
            title={tituloDetalle}
            extra={(
              <>
                <Button
                  type="link"
                  icon={<ArrowLeftOutlined />}
                  style={btnLinkStyle}
                  onClick={onRegresar}
                >
                  Regresar
                </Button>
                <Button
                  type="link"
                  icon={<SaveOutlined />}
                  style={btnLinkStyle}
                  onClick={onGuardarCabecera}
                  disabled={esAutorizado}
                >
                  Guardar
                </Button>
                {currentCabecera?.estados_globales === 1 && (
                  <Button
                    type="link"
                    icon={<CheckCircleOutlined />}
                    style={{
                      ...btnLinkStyle,
                      color: puedeAutorizar ? '#52c41a' : undefined,
                    }}
                    onClick={onAutorizar}
                    disabled={!puedeAutorizar}
                  >
                    Autorizar
                  </Button>
                )}
              </>
            )}
            bordered={false}
          >
            <Tabs
              activeKey={activeTab}
              onChange={tabBloqueado ? undefined : setActiveTab}
              type="card"
            >
              <TabPane
                tab="Modificaciones"
                key="modificaciones"
                disabled={tabBloqueado && activeTab !== 'modificaciones'}
              >
                {cabeceraForm}
                <Divider />
                {!esAutorizado && (
                  <Row gutter={8} style={{ marginBottom: 8 }}>
                    <Col>
                      <Button type="link" icon={<PlusOutlined />} onClick={onClickAddRegistro}>
                        Agregar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => onClickEditRegistro()}
                        disabled={!selectedRegistroKeys.length}
                      >
                        Editar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        type="link"
                        icon={<DeleteOutlined />}
                        onClick={onClickDeleteRegistro}
                        disabled={!selectedRegistroKeys.length}
                      >
                        Eliminar
                      </Button>
                    </Col>
                  </Row>
                )}
                <AntTable
                  columns={registroColumns}
                  dataSource={registros}
                  rowKey="id"
                  size="small"
                  rowSelection={registroRowSelection}
                  onRow={(record) => ({
                    onClick: () => handleRegistroRowClick(record),
                    onDoubleClick: () => onClickEditRegistro(record),
                    style: { cursor: 'pointer' },
                  })}
                  pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
                  scroll={{ x: 'max-content' }}
                />
              </TabPane>

              <TabPane
                tab="Traspasos"
                key="traspasos"
                disabled={tabBloqueado && activeTab !== 'traspasos'}
              >
                {cabeceraForm}
                <Divider />
                {!esAutorizado && (
                  <Row gutter={8} style={{ marginBottom: 8 }}>
                    <Col>
                      <Button type="link" icon={<PlusOutlined />} onClick={onClickAddRegistro}>
                        Agregar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => onClickEditRegistro()}
                        disabled={!selectedRegistroKeys.length}
                      >
                        Editar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        type="link"
                        icon={<DeleteOutlined />}
                        onClick={onClickDeleteRegistro}
                        disabled={!selectedRegistroKeys.length}
                      >
                        Eliminar
                      </Button>
                    </Col>
                  </Row>
                )}
                <AntTable
                  columns={registroColumns}
                  dataSource={registros}
                  rowKey="id"
                  size="small"
                  rowSelection={registroRowSelection}
                  onRow={(record) => ({
                    onClick: () => handleRegistroRowClick(record),
                    onDoubleClick: () => onClickEditRegistro(record),
                    style: { cursor: 'pointer' },
                  })}
                  pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
                  scroll={{ x: 'max-content' }}
                />
              </TabPane>
            </Tabs>
          </Card>
        )}

        {/* ── MODAL: Agregar / Editar Registro ── */}
        <Modal
          open={registroModalVisible}
          closable={false}
          maskClosable={!modalLoading}
          keyboard={!modalLoading}
          footer={null}
          width={720}
          title={(
            <FormSubmitControls
              label={`${editingRegistro ? 'Editar' : 'Agregar'} Registro`}
              onFinish={onFinishRegistro}
              onCancel={() => {
                setRegistroModalVisible(false);
                formRegistro.resetFields();
                setEditingRegistro(null);
              }}
              loading={modalLoading}
            />
          )}
          onCancel={() => {
            setRegistroModalVisible(false);
            formRegistro.resetFields();
            setEditingRegistro(null);
          }}
          forceRender
        >
          <Spin spinning={modalLoading}>
            <Form layout="vertical" form={formRegistro} scrollToFirstError>
              <Row gutter={12}>
                <Col xs={24} sm={8}>
                  <Form.Item label="Fecha">
                    <Input
                      value={currentCabecera?.fecha
                        ? dayjs(currentCabecera.fecha).format('DD/MM/YYYY')
                        : ''}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Folio Agrupador">
                    <Input value={currentCabecera?.folio_agrupador || ''} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Folio Presupuestal">
                    <Input value={currentCabecera?.folio || ''} disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name="cog" label="Clasificador por Objeto del Gasto" rules={[requiredRule]}>
                    <TreeSelect
                      showSearch
                      showArrow
                      treeNodeFilterProp="title"
                      treeData={catalogoCOGTree}
                      style={{ width: '100%' }}
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                      allowClear
                      onChange={(val) => {
                        const area = formRegistro.getFieldValue('area');
                        const fuente = formRegistro.getFieldValue('fuente_de_financiamiento');
                        loadClavesFiltradas(
                          Number(extractIdClasificacionObjetoGasto(val)),
                          area,
                          Number(extractIdFuenteDeFinanciamiento(fuente)),
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name="area" label="Área" rules={areaAplica ? [requiredRule] : []}>
                    <TreeSelect
                      showSearch
                      showArrow
                      treeNodeFilterProp="title"
                      treeData={catalogoAreasTree}
                      style={{ width: '100%' }}
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                      allowClear
                      disabled={!areaAplica}
                      onChange={(val) => {
                        const cog = formRegistro.getFieldValue('cog');
                        const fuente = formRegistro.getFieldValue('fuente_de_financiamiento');
                        loadClavesFiltradas(
                          Number(extractIdClasificacionObjetoGasto(cog)),
                          val,
                          Number(extractIdFuenteDeFinanciamiento(fuente)),
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name="fuente_de_financiamiento" label="Fuente de Financiamiento" rules={[requiredRule]}>
                    <TreeSelect
                      showSearch
                      showArrow
                      treeNodeFilterProp="title"
                      treeData={catalogoFuentesTree}
                      style={{ width: '100%' }}
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                      allowClear
                      onChange={(val) => {
                        const cog = formRegistro.getFieldValue('cog');
                        const area = formRegistro.getFieldValue('area');
                        loadClavesFiltradas(
                          Number(extractIdClasificacionObjetoGasto(cog)),
                          area,
                          Number(extractIdFuenteDeFinanciamiento(val)),
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item
                    name="clave_presupuestal"
                    label="Clave Presupuestal"
                    rules={[requiredRule]}
                  >
                    <Select
                      dataSource={catalogoClaves}
                      labelProp="label_clave"
                      keyProp="id"
                      disabled={!clavesHabilitadas}
                      onChange={onChangeClave}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item
                    name="cuenta_contable"
                    label="Cuenta Contable"
                    rules={[requiredRule]}
                  >
                    <TreeSelect
                      showSearch
                      showArrow
                      treeNodeFilterProp="title"
                      treeData={catalogoCuentasContables}
                      style={{ width: '100%' }}
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                      allowClear
                      disabled={cuentaBloqueada}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name="periodo" label="Periodo">
                    <Select
                      dataSource={MESES}
                      labelProp="nombre"
                      keyProp="id"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name="presupuesto_anterior" label="Presupuesto Anterior">
                    <InputNumber
                      disabled
                      style={{ width: '100%' }}
                      precision={2}
                      formatter={moneyFormatter}
                      parser={moneyParser}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Form.Item name="aumento_de_importe" label="Aumento">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      formatter={moneyFormatter}
                      parser={moneyParser}
                      placeholder="$ 0.00"
                      onChange={calcularPresupuestoVigente}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="disminucion_de_importe" label="Disminución">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      formatter={moneyFormatter}
                      parser={moneyParser}
                      placeholder="$ 0.00"
                      onChange={calcularPresupuestoVigente}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name="presupuesto_vigente" label="Presupuesto Vigente">
                    <InputNumber
                      disabled
                      style={{ width: '100%' }}
                      precision={2}
                      formatter={moneyFormatter}
                      parser={moneyParser}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name="elaboro_display" label="Elaboró">
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Form.Item name="plaza_display" label="Plaza Elaboró">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="area_display" label="Área Elaboró">
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name="estados_globales" label="Estado">
                    <Select
                      dataSource={estadosGlobales}
                      labelProp="descripcion"
                      keyProp="id"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Modal>

        {/* ── MODAL: Eliminar Cabecera ── */}
        <Modal
          open={deleteCabeceraVisible}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label="Eliminar"
              onFinish={onConfirmDeleteCabecera}
              onCancel={() => setDeleteCabeceraVisible(false)}
              loading={loading}
              baseIcon={<DeleteOutlined />}
              finishText="Eliminar"
            />
          )}
          onCancel={() => setDeleteCabeceraVisible(false)}
          forceRender
        >
          <Typography.Text>
            {`¿Desea eliminar el registro ${
              cabeceras.find((c) => c.id === selectedCabeceraKeys[0])
                ?.folio_agrupador || selectedCabeceraKeys[0] || ''
            }?`}
          </Typography.Text>
          <br />
          <Typography.Text>
            Solo será posible si no ha sido utilizado en otros procesos.
          </Typography.Text>
        </Modal>

        {/* ── MODAL: Eliminar Registro ── */}
        <Modal
          open={deleteRegistroVisible}
          closable={false}
          maskClosable={!modalLoading}
          keyboard={!modalLoading}
          footer={null}
          title={(
            <FormSubmitControls
              label="Eliminar Registro"
              onFinish={onConfirmDeleteRegistro}
              onCancel={() => setDeleteRegistroVisible(false)}
              loading={modalLoading}
              baseIcon={<DeleteOutlined />}
              finishText="Eliminar"
            />
          )}
          onCancel={() => setDeleteRegistroVisible(false)}
          forceRender
        >
          <Typography.Text>¿Desea eliminar este registro?</Typography.Text>
          <br />
          <Typography.Text>
            Solo será posible si no ha sido utilizado en otros procesos.
          </Typography.Text>
        </Modal>

      </Spin>
    </Row>
  );
}

ModificacionesPresupuestales.propTypes = {
  permission: PropTypes.shape({}),
};

ModificacionesPresupuestales.defaultProps = {
  permission: {},
};

export const permissionModificacionesPresupuestalesNomina = {
  permissionModel: 'traspasoomodificacionpresupuestaldeegresos',
  subModels: ['registrodetraspasoomodificacionpresupuestaldeegresos'],
  // Catálogos cuyo listado exige view_ en el backend (sin ellos no cargan las claves ni los COG)
  catalogsModels: ['clavepresupuestal', 'clasificaciondeobjetodegastointerno'],
};

export default ModificacionesPresupuestales;
